import React from 'react';

export interface PageStateProps {
  loading: boolean;
  error: string | null | undefined;
  /**
   * Whether usable data is already available. When data exists, stale
   * content is preferred over an error or loading screen.
   */
  hasData: boolean;
  /**
   * Container className - default: "max-w-4xl mx-auto"
   */
  className?: string;
  /**
   * Render function for the loading state
   */
  renderLoading: () => React.ReactNode;
  /**
   * Render function for the error state
   */
  renderError: () => React.ReactNode;
  /**
   * Render function for the success state (including empty data - the
   * empty-state decision is data-shaped and belongs to the page)
   */
  renderContent: () => React.ReactNode;
}

/**
 * Handles the common page states (loading, error, success) and eliminates
 * boilerplate across pages.
 *
 * Precedence: error (only when there is no data to show) → loading (only
 * when there is no data to show) → content.
 */
export function PageState({
  loading,
  error,
  hasData,
  className = 'max-w-4xl mx-auto',
  renderLoading,
  renderError,
  renderContent,
}: PageStateProps): React.ReactElement {
  // Fade content in ONLY when it replaces the skeleton (data arriving on an
  // already-mounted page). On route changes the view transition owns the
  // entrance — fading here as well would double-animate the main area.
  // Render-phase state update is the sanctioned derive-from-props pattern.
  const [sawLoading, setSawLoading] = React.useState(false);
  if (loading && !hasData && !sawLoading) {
    setSawLoading(true);
  }

  let content: React.ReactNode;
  if (error && !hasData) {
    content = renderError();
  } else if (loading && !hasData) {
    content = renderLoading();
  } else if (sawLoading) {
    content = (
      <div className="animate-in duration-300 fade-in">{renderContent()}</div>
    );
  } else {
    content = renderContent();
  }

  return <div className={className}>{content}</div>;
}
